import { useRef, useState, useCallback, useEffect } from 'react';

const DEFAULT_WS_URL = 'ws://localhost:9001/ws';

function isCommandSuccess(result) {
    if (!result) return false;
    if (result.Success === true || result.success === true) return true;
    const status = (result.Status || result.status || '').toString().toLowerCase();
    return status === 'connected' || status === 'ok' || status === 'success';
}


export function useRfidAgent(wsUrl = process.env.REACT_APP_RFID_WS_URL || DEFAULT_WS_URL) {
    const wsRef = useRef(null);
    const connectingRef = useRef(false);
    const onTagRef = useRef(null);
    const onCommandResultRef = useRef(null);

    const [connectionStatus, setConnectionStatus] = useState('disconnected');
    const [readerConnected, setReaderConnected] = useState(false);
    const [scannedTags, setScannedTags] = useState([]);
    const [logMessages, setLogMessages] = useState([]);

    const addLog = useCallback((msg) => {
        setLogMessages((prev) => [...prev.slice(-99), msg]);
    }, []);

    const sendCommand = useCallback((action) => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ action }));
        }
    }, []);

    const handleMessage = useCallback((event) => {
        let data;
        try {
            data = JSON.parse(event.data);
        } catch (err) {
            addLog(`parse error: ${err.message}`);
            return;
        }

        addLog(JSON.stringify(data));

        if (data.type === 'error') {
            return;
        }

        if (data.type === 'command_result') {
            const success = isCommandSuccess(data.result);

            if (data.action === 'connect') {
                connectingRef.current = false;
                setReaderConnected(success);
                setConnectionStatus(success ? 'connected' : 'error');
            }

            if (data.action === 'disconnect') {
                setReaderConnected(false);
                setConnectionStatus('disconnected');
                connectingRef.current = false;
            }

            onCommandResultRef.current?.(data);
            return;
        }

        if (data.type === 'tag' && data.decodeStatus === 'ok' && data.stockId) {
            const stockId = String(data.stockId);
            setScannedTags((prev) => [...prev, stockId]);
            onTagRef.current?.(stockId);
        }
    }, [addLog]);

    const openWebSocket = useCallback(() => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
            return Promise.resolve();
        }

        if (wsRef.current?.readyState === WebSocket.CONNECTING) {
            return new Promise((resolve, reject) => {
                const ws = wsRef.current;
                const onOpen = () => {
                    ws.removeEventListener('open', onOpen);
                    ws.removeEventListener('error', onError);
                    resolve();
                };
                const onError = () => {
                    ws.removeEventListener('open', onOpen);
                    ws.removeEventListener('error', onError);
                    reject(new Error('WebSocket error'));
                };
                ws.addEventListener('open', onOpen);
                ws.addEventListener('error', onError);
            });
        }

        return new Promise((resolve, reject) => {
            const ws = new WebSocket(wsUrl);
            wsRef.current = ws;

            ws.onopen = () => resolve();
            ws.onmessage = handleMessage;
            ws.onerror = () => {
                setConnectionStatus('error');
                connectingRef.current = false;
                reject(new Error('WebSocket error'));
            };
            ws.onclose = () => {
                setConnectionStatus('disconnected');
                setReaderConnected(false);
                connectingRef.current = false;
                if (wsRef.current === ws) {
                    wsRef.current = null;
                }
            };
        });
    }, [wsUrl, handleMessage]);

    const connect = useCallback(async () => {
        if (connectingRef.current) return;
        if (readerConnected && wsRef.current?.readyState === WebSocket.OPEN) return;

        connectingRef.current = true;
        setConnectionStatus('connecting');

        try {
            await openWebSocket();
            sendCommand('connect');
        } catch (err) {
            setConnectionStatus('error');
            connectingRef.current = false;
            throw err;
        }
    }, [openWebSocket, sendCommand, readerConnected]);

    const disconnect = useCallback(() => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
            sendCommand('disconnect');
        }
        if (wsRef.current) {
            wsRef.current.close();
            wsRef.current = null;
        }
        setReaderConnected(false);
        setConnectionStatus('disconnected');
        setScannedTags([]);
        connectingRef.current = false;
    }, [sendCommand]);

    const startScan = useCallback(() => sendCommand('start_scan'), [sendCommand]);
    const stopScan = useCallback(() => sendCommand('stop_scan'), [sendCommand]);

    const setOnTag = useCallback((cb) => {
        onTagRef.current = cb;
    }, []);

    const setOnCommandResult = useCallback((cb) => {
        onCommandResultRef.current = cb;
    }, []);

    useEffect(() => {
        return () => {
            if (wsRef.current) {
                wsRef.current.close();
                wsRef.current = null;
            }
        };
    }, []);

    return {
        connectionStatus,
        readerConnected,
        scannedTags,
        logMessages,
        connect,
        disconnect,
        startScan,
        stopScan,
        setOnTag,
        setOnCommandResult,
        isConnecting: connectionStatus === 'connecting',
    };
}
